import type { SectionStatus } from "./SectionAnchorNav.js";
import { AlertTriangle, Mail, CheckCircle2 } from "lucide-react";
import { AsyncButton } from "../ui/async-button.js";

export interface HRSectionStatus {
  id: string;
  label: string;
  status: SectionStatus;
}

interface Props {
  completionPct: number;
  sectionStatuses: HRSectionStatus[];
  conflictCount?: number | undefined;
  notes: string;
  onNotesChange: (v: string) => void;
  onSectionClick: (id: string) => void;
  onSendToEmployee?: (() => void) | undefined;
  onSubmit: () => Promise<void>;
  submitLabel?: string | undefined;
  submitDisabled: boolean;
  submitReason?: string | undefined;
  saving: boolean;
  onCancel: () => void;
  /** View-only profile: hides the submit button and labels Cancel as "Back". */
  readOnly?: boolean | undefined;
}

const statusColor: Record<SectionStatus, string> = {
  complete: "var(--color-success-accent)", error: "var(--color-error-accent)", partial: "var(--color-warning-accent)", empty: "var(--color-surface-400)",
};
const statusLabel: Record<SectionStatus, string> = {
  complete: "Complete", error: "Has errors", partial: "Incomplete", empty: "Not started",
};

interface MiniProfileBarProps {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  designation: string;
  dept: string;
  office: string;
}

// The employee's avatar/name/role, shown as its own fixed row directly under the page
// header — kept out of the scrolling area so it's always visible, same as the header.
//
// Its height is pinned to the centre column's header (14px padding + a 32px `sm`
// button = 60px), so the rule under this bar lines up exactly with the rule under
// the "Expand all" row next to it. That is why role, department and office share
// one line: three stacked lines would not fit the 60px.
export function EmployeeMiniProfileBar({ firstName, middleName, lastName, designation, dept, office }: MiniProfileBarProps) {
  const initials = [firstName[0], lastName[0]].filter(Boolean).join("").toUpperCase() || "?";
  const name = [firstName, middleName, lastName].filter(Boolean).join(" ") || "New Employee";
  const meta = [designation, dept, office].filter(Boolean).join(" · ");

  return (
    <div className="flex min-h-[60px] shrink-0 items-center gap-2.5 border-b border-surface-200 bg-surface-0 px-5">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[13px] font-bold text-brand-700">
        {initials}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[13px] font-bold leading-4 text-surface-900">{name}</div>
        {meta && <div className="truncate text-[11px] leading-[14px] text-surface-500">{meta}</div>}
      </div>
    </div>
  );
}

export function HRReviewPanel({
  completionPct, sectionStatuses, conflictCount,
  notes, onNotesChange, onSectionClick,
  onSendToEmployee, onSubmit, submitLabel, submitDisabled, submitReason, saving, onCancel, readOnly = false,
}: Props) {
  return (
    // The panel fills the column: the mini profile above it and the actions below stay put,
    // only the middle scrolls when the viewport is too short for the whole panel.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {/* Completion bar */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-surface-600)" }}>Profile completion</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-brand-600)" }}>{completionPct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "var(--color-surface-100)" }}>
            <div style={{
              height: 6, borderRadius: 3, width: `${completionPct}%`,
              background: completionPct === 100 ? "var(--color-success)" : "var(--color-brand-500)",
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>

        {/* Conflicts badge */}
        {(conflictCount ?? 0) > 0 && (
          <div style={{ padding: "8px 12px", background: "var(--color-error-bg)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-error-border)" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-error-fg)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} /> {conflictCount} data conflict{(conflictCount ?? 0) > 1 ? "s" : ""} detected
            </span>
            <p style={{ fontSize: 11, color: "var(--color-error-fg)", margin: "2px 0 0" }}>Resolve before creating employee</p>
          </div>
        )}

        {/* Section status */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-400)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Section Status
          </p>
          {sectionStatuses.map(s => (
            <button
              key={s.id}
              onClick={() => onSectionClick(s.id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "5px 0",
                background: "none", border: "none", cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 12, color: "var(--color-surface-700)" }}>{s.label}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: statusColor[s.status] }}>
                {statusLabel[s.status]}
              </span>
            </button>
          ))}
        </div>

        {/* HR Notes */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-surface-600)", display: "block", marginBottom: 4 }}>
            HR Notes (internal)
          </label>
          <textarea
            value={notes}
            onChange={e => onNotesChange(e.target.value)}
            placeholder="Internal notes — never shown to employee"
            rows={3}
            style={{
              width: "100%", padding: "6px 8px", fontSize: 12,
              border: "1px solid var(--color-surface-200)",
              borderRadius: "var(--radius-md)", resize: "vertical",
              background: "var(--color-surface-0)", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Send to Employee */}
        {onSendToEmployee && (
          <div>
            <button
              onClick={onSendToEmployee}
              style={{
                width: "100%", padding: "8px 16px",
                background: "var(--color-surface-0)", color: "var(--color-brand-600)",
                border: "1.5px solid var(--color-brand-300)", borderRadius: "var(--radius-md)",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <Mail size={14} /> Send to Employee →
            </button>
            <p style={{ fontSize: 11, color: "var(--color-surface-400)", marginTop: 4, textAlign: "center" }}>
              Employee fills personal info, address, bank
            </p>
          </div>
        )}

      </div>

      {/* Submit — pinned to the bottom of the panel */}
      <div className="shrink-0 border-t border-surface-200 bg-surface-0 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="shrink-0"
            style={{
              padding: "8px 14px",
              background: "var(--color-surface-0)", color: "var(--color-surface-700)",
              border: "1px solid var(--color-surface-300)", borderRadius: "var(--radius-md)",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            {readOnly ? "Back" : "Cancel"}
          </button>
          {!readOnly && <AsyncButton
            onClick={() => onSubmit()}
            confirm={{
              title: submitLabel ? "Save these changes?" : "Create this employee?",
              description: submitLabel
                ? "Writes every field you have edited across all sections to this employee's record."
                : "Creates the employee record from the details entered in the form.",
              actionText: submitLabel ?? "Create Employee",
            }}
            disabled={submitDisabled || saving}
            title={submitReason}
            className="min-w-0 flex-1"
          >
            <CheckCircle2 size={14} /> {submitLabel ?? "Create Employee"}
          </AsyncButton>}
        </div>
        {submitReason && (
          <p style={{ fontSize: 11, color: "var(--color-error)", marginTop: 6, textAlign: "center" }}>{submitReason}</p>
        )}
      </div>
    </div>
  );
}
